import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { SLIDES_LIBRARY_TYPE_KEY } from "../constants";
import type { SlidesLibraryActions, SlidesLibraryState } from "./types";

const emptyMultiSelect = () => ({
  multiSelectSlides: new Map() as SlidesLibraryState["multiSelectSlides"],
  selectedSlideIds: new Set() as SlidesLibraryState["selectedSlideIds"],
  hasUnsavedSlides: false,
});

const initialState: SlidesLibraryState = {
  type: "templates",
  folderId: "",
  search: "",
  isSearching: false,
  activeTemplateCategoryId: "",
  isManualScrolling: false,
  view: "editor",
  multiSelectSlides: new Map(),
  selectedSlideIds: new Set(),
  hasUnsavedSlides: false,
  templateTab: "single-slides",
  onSuccessCallback: undefined,
  onPickSlidesCallback: undefined,
  createTarget: "story",
  importStoriesFolderId: "",
  importStoriesSource: "my-stories",
  importStoriesKind: "stories",
  importStoriesSelectedKind: "story",
  importStoriesTemplateCategoryId: "",
  importStoriesSelectedStoryId: "",
  importStoriesSelectedStoryTitle: "",
  importStoriesContentPointer: "",
  expandedSavedTabs: {},
};

export const useSlidesLibraryStore = create<
  SlidesLibraryState & SlidesLibraryActions
>((set, get) => ({
  ...initialState,
  setView: (view) => set({ view }),
  setCreateTarget: (createTarget) => set({ createTarget }),
  setActiveTemplateCategoryId: (activeTemplateCategoryId) =>
    set({ activeTemplateCategoryId, folderId: "" }),
  setIsSearching: (isSearching) => set({ isSearching }),
  setSearch: (search) => {
    const isImportFromStories = get().templateTab === "import-from-stories";
    set({ search, isSearching: !isImportFromStories });
  },
  setFolderId: (folderId) => set({ folderId, activeTemplateCategoryId: "" }),
  toggleSavedTab: (key) =>
    set((state) => ({
      expandedSavedTabs: {
        ...state.expandedSavedTabs,
        [key]: !state.expandedSavedTabs[key],
      },
    })),
  setType: (type) => {
    const payload = {
      type,
      folderId: "",
      isSearching: false,
      ...emptyMultiSelect(),
    };
    if (type === "team-slides") payload.folderId = "team-slides";
    if (type === "user-slides") payload.folderId = "user-slides";
    if (type === "recently-deleted") payload.folderId = "recently-deleted";
    if (get().search) payload.isSearching = true;
    window.localStorage.setItem(SLIDES_LIBRARY_TYPE_KEY, type);
    set(payload);
  },
  setIsManualScrolling: (isManualScrolling) => set({ isManualScrolling }),
  resetMultiSelectSlides: () => set(emptyMultiSelect()),
  handleMultiselectSlide: (slide, action) => {
    const currentMap = get().multiSelectSlides;
    const currentSet = get().selectedSlideIds;

    let newMultiSelectSlides: SlidesLibraryState["multiSelectSlides"];
    let newSelectedSlideIds: SlidesLibraryState["selectedSlideIds"];

    if (action === "add") {
      newMultiSelectSlides = new Map(currentMap);
      newMultiSelectSlides.set(slide.id, slide);
      newSelectedSlideIds = new Set(currentSet);
      newSelectedSlideIds.add(slide.id);
    } else {
      newMultiSelectSlides = new Map(currentMap);
      newMultiSelectSlides.delete(slide.id);
      newSelectedSlideIds = new Set(currentSet);
      newSelectedSlideIds.delete(slide.id);
    }

    const hasUnsavedSlides = Array.from(newMultiSelectSlides.values()).some(
      (s) => s.type === "template",
    );

    set({
      multiSelectSlides: newMultiSelectSlides,
      selectedSlideIds: newSelectedSlideIds,
      hasUnsavedSlides,
    });
  },
  isSelected: (slideId: string) => get().selectedSlideIds.has(slideId),
  isInMultiSelectMode: (isIntersecting: boolean) =>
    isIntersecting && get().selectedSlideIds.size > 0,
  setTemplateTab: (templateTab) => set({ templateTab }),
  setOnSuccess: (onSuccess) => set({ onSuccessCallback: onSuccess }),
  setOnPickSlides: (onPickSlides) =>
    set({ onPickSlidesCallback: onPickSlides }),
  setImportStoriesFolderId: (importStoriesFolderId) =>
    set({ importStoriesFolderId }),
  setImportStoriesSource: (importStoriesSource) => set({ importStoriesSource }),
  setImportStoriesKind: (importStoriesKind) => set({ importStoriesKind }),
  setImportStoriesTemplateCategoryId: (importStoriesTemplateCategoryId) =>
    set({ importStoriesTemplateCategoryId }),
  setImportStoriesSelectedStory: (id, title, contentPointer, kind = "story") =>
    set({
      importStoriesSelectedStoryId: id,
      importStoriesSelectedStoryTitle: title,
      importStoriesContentPointer: contentPointer || "",
      importStoriesSelectedKind: kind,
      ...emptyMultiSelect(),
    }),
  clearImportStoriesSelectedStory: () =>
    set({
      importStoriesSelectedStoryId: "",
      importStoriesSelectedStoryTitle: "",
      importStoriesContentPointer: "",
      ...emptyMultiSelect(),
    }),
  reset: () => set(initialState),
}));

export const useSlidesLibraryStoreShallow = <U>(
  selector: (state: SlidesLibraryState & SlidesLibraryActions) => U,
) => useSlidesLibraryStore(useShallow(selector));
